import React from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { onIdTokenChanged } from 'firebase/auth';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { auth, db, functions } from '../firebase';

export default function AdminUsersPage() {
  const [isSystemAdmin, setIsSystemAdmin] = React.useState(false);
  const [authChecked, setAuthChecked] = React.useState(false);
  const [admins, setAdmins] = React.useState([]);
  const [domains, setDomains] = React.useState([]);
  const [loadingAdmins, setLoadingAdmins] = React.useState(true);
  const [formState, setFormState] = React.useState({
    email: '',
    name: '',
    password: '',
    role: 'SYSTEM_ADMIN',
    domainId: '',
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [editId, setEditId] = React.useState(null);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [generatedPassword, setGeneratedPassword] = React.useState('');

  React.useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        setIsSystemAdmin(false);
        setAuthChecked(true);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        setIsSystemAdmin(tokenResult?.claims?.role === 'SYSTEM_ADMIN');
      } catch (err) {
        console.warn('Failed to check claims', err);
        setIsSystemAdmin(false);
      } finally {
        setAuthChecked(true);
      }
    });
    return () => unsubscribe();
  }, []);

  React.useEffect(() => {
    if (!isSystemAdmin) {
      setAdmins([]);
      setDomains([]);
      setLoadingAdmins(false);
      return undefined;
    }
    setLoadingAdmins(true);

    const dUnsub = onSnapshot(collection(db, 'domains'), (snap) => {
      setDomains(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const q = query(collection(db, 'admins'), orderBy('email'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAdmins(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLoadingAdmins(false);
    }, (err) => {
      console.error('Failed to load admins', err);
      setError(err.message || 'Failed to load admins');
      setLoadingAdmins(false);
    });
    return () => {
      unsubscribe();
      dUnsub();
    };
  }, [isSystemAdmin]);

  const handleChange = (field) => (event) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleEdit = (adminUser) => {
    setEditId(adminUser.id);
    setFormState({
      email: adminUser.email || '',
      name: adminUser.name || '',
      password: '',
      role: adminUser.role || 'SYSTEM_ADMIN',
      domainId: adminUser.domainId || '',
    });
    setError('');
    setSuccess('');
    setGeneratedPassword('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormState({ email: '', name: '', password: '', role: 'SYSTEM_ADMIN', domainId: '' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    setGeneratedPassword('');

    if (formState.role === 'DOMAIN_ADMIN' && !formState.domainId) {
      setError('Please select a domain for the Domain Admin.');
      return;
    }

    setSubmitting(true);
    try {
      if (editId) {
        const callable = httpsCallable(functions, 'updateAdminUser');
        await callable({
          adminId: editId,
          name: formState.name || undefined,
          role: formState.role,
          domainId: formState.role === 'DOMAIN_ADMIN' ? formState.domainId : undefined,
        });
        setSuccess(`Updated admin user ${formState.email}`);
        setEditId(null);
      } else {
        const callable = httpsCallable(functions, 'createAdminUser');
        const payload = {
          email: formState.email,
          name: formState.name || undefined,
          role: formState.role,
          domainId: formState.role === 'DOMAIN_ADMIN' ? formState.domainId : undefined,
        };
        if (formState.password.trim()) {
          payload.password = formState.password.trim();
        }
        const result = await callable(payload);
        const data = result?.data || {};
        setSuccess(`Created admin user for ${data.email}`);
        if (data.temporaryPassword) {
          setGeneratedPassword(data.temporaryPassword);
        }
      }
      setFormState({ email: '', name: '', password: '', role: 'SYSTEM_ADMIN', domainId: '' });
    } catch (err) {
      console.error('Admin operation failed', err);
      const message = err?.details || err?.message || 'Operation failed';
      setError(typeof message === 'string' ? message : 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (adminUser) => {
    if (!window.confirm(`Reset password for ${adminUser.email || adminUser.id}? A new temporary password will be generated.`)) return;

    setError('');
    setSuccess('');
    setGeneratedPassword('');
    setSubmitting(true);
    try {
      const callable = httpsCallable(functions, 'resetAdminPassword');
      const result = await callable({ adminId: adminUser.id });
      const data = result?.data || {};

      setSuccess(`Password reset successfully for ${adminUser.email || adminUser.id}`);
      if (data.temporaryPassword) {
        setGeneratedPassword(data.temporaryPassword);
      }
    } catch (err) {
      console.error('resetAdminPassword failed', err);
      const message = err?.details || err?.message || 'Failed to reset password';
      setError(typeof message === 'string' ? message : 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (adminUser) => {
    const action = adminUser.disabled ? 'Enable' : 'Disable';
    if (!window.confirm(`${action} user ${adminUser.email || adminUser.id}?`)) return;

    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const callable = httpsCallable(functions, 'setAdminDisabledStatus');
      await callable({ adminId: adminUser.id, disabled: !adminUser.disabled });
      setSuccess(`User ${adminUser.email || adminUser.id} has been ${adminUser.disabled ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      console.error('setAdminDisabledStatus failed', err);
      const message = err?.details || err?.message || `Failed to ${action.toLowerCase()} user`;
      setError(typeof message === 'string' ? message : `Failed to ${action.toLowerCase()} user`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdmin = async (adminUser) => {
    if (!window.confirm(`PERMANENTLY DELETE user ${adminUser.email || adminUser.id}? This cannot be undone.`)) return;

    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const callable = httpsCallable(functions, 'deleteAdminUser');
      await callable({ adminId: adminUser.id });
      setSuccess(`User ${adminUser.email || adminUser.id} has been deleted.`);
    } catch (err) {
      console.error('deleteAdminUser failed', err);
      const message = err?.details || err?.message || 'Failed to delete user';
      setError(typeof message === 'string' ? message : 'Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const getDomainName = (id) => {
    const domain = domains.find(d => d.id === id);
    return domain ? domain.name : id;
  };

  if (!authChecked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isSystemAdmin) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Restricted Access</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Only System Admins may manage admin users.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{editId ? 'Edit Admin User' : 'Create Admin User'}</Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Email"
            type="email"
            value={formState.email}
            onChange={handleChange('email')}
            required
            disabled={submitting || !!editId}
            helperText={editId ? "Email cannot be changed" : ""}
          />
          <TextField
            label="Name"
            value={formState.name}
            onChange={handleChange('name')}
            disabled={submitting}
          />
          <FormControl>
            <InputLabel>Role</InputLabel>
            <Select
              value={formState.role}
              label="Role"
              onChange={handleChange('role')}
              disabled={submitting}
            >
              <MenuItem value="SYSTEM_ADMIN">System Admin</MenuItem>
              <MenuItem value="DOMAIN_ADMIN">Domain Admin</MenuItem>
            </Select>
          </FormControl>
          {formState.role === 'DOMAIN_ADMIN' && (
            <FormControl>
              <InputLabel>Domain</InputLabel>
              <Select
                value={formState.domainId}
                label="Domain"
                onChange={handleChange('domainId')}
                disabled={submitting}
              >
                {domains.map(d => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!editId && (
            <TextField
              label="Temporary Password (optional)"
              value={formState.password}
              onChange={handleChange('password')}
              helperText="Leave blank to auto-generate a strong password"
              disabled={submitting}
            />
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Saving…' : (editId ? 'Update Admin' : 'Create Admin')}
            </Button>
            {editId && (
              <Button variant="outlined" onClick={handleCancelEdit} disabled={submitting}>
                Cancel
              </Button>
            )}
            {submitting && <CircularProgress size={24} />}
          </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
        {generatedPassword && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Temporary password: <strong>{generatedPassword}</strong>
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Existing Admin Users</Typography>
        {loadingAdmins ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No admin users found.</TableCell>
                </TableRow>
              ) : (
                admins.map((adminUser) => (
                  <TableRow key={adminUser.id} sx={{ opacity: adminUser.disabled ? 0.6 : 1 }}>
                    <TableCell>{adminUser.email || adminUser.id}</TableCell>
                    <TableCell>{adminUser.name || 'N/A'}</TableCell>
                    <TableCell>{adminUser.role === 'DOMAIN_ADMIN' ? 'Domain Admin' : 'System Admin'}</TableCell>
                    <TableCell>{adminUser.domainId ? getDomainName(adminUser.domainId) : 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={adminUser.disabled ? 'Disabled' : 'Active'}
                        color={adminUser.disabled ? 'default' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleEdit(adminUser)}
                          disabled={submitting}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleResetPassword(adminUser)}
                          disabled={submitting}
                        >
                          Reset Pass
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color={adminUser.disabled ? 'success' : 'warning'}
                          onClick={() => handleToggleStatus(adminUser)}
                          disabled={submitting}
                        >
                          {adminUser.disabled ? 'Enable' : 'Disable'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteAdmin(adminUser)}
                          disabled={submitting || adminUser.id === auth.currentUser?.uid}
                        >
                          Remove
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
